/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';

// Pure counting helpers for the catalogue page. Deliberately free of MobX, store and
// helpers/api imports so counting can never trigger a request or a store read.

const TERMINATED = 'TERMINATED';

// Shared, frozen zero result handed back for any envTypeId we have no records for.
const ZERO = Object.freeze({ deployed: 0, terminated: 0 });

// One pass over the records. Deployed is deliberately "anything that is not TERMINATED",
// so TERMINATING and TERMINATING_FAILED count as deployed.
function buildEnvTypeCountMap(scEnvironments = []) {
  const map = Object.create(null); // avoids prototype-key collisions on arbitrary ids
  _.forEach(scEnvironments, env => {
    const envTypeId = _.get(env, 'envTypeId');
    if (_.isEmpty(envTypeId)) return;
    let entry = map[envTypeId];
    if (!entry) {
      entry = { deployed: 0, terminated: 0 };
      map[envTypeId] = entry;
    }
    if (_.get(env, 'status') === TERMINATED) entry.terminated += 1;
    else entry.deployed += 1;
  });
  return map;
}

function getVersionCounts(countMap, productId, artifactId) {
  return countMap[`${productId}-${artifactId}`] || ZERO;
}

function getProductTotals(countMap, productId) {
  const prefix = `${productId}-`;
  let deployed = 0;
  let terminated = 0;
  _.forEach(_.keys(countMap), key => {
    if (!_.startsWith(key, prefix)) return;
    deployed += countMap[key].deployed;
    terminated += countMap[key].terminated;
  });
  return { deployed, terminated };
}

// The value object handed down to cards and rows. Product totals are memoised on first
// access so a page of N cards costs one pass over the records plus one pass over the
// envTypeId keys per product, not per row.
function createCatalogueCounts(scEnvironments = []) {
  const byEnvTypeId = buildEnvTypeCountMap(scEnvironments);
  const productCache = Object.create(null);
  return {
    byEnvTypeId,
    getVersionCounts: (productId, artifactId) => getVersionCounts(byEnvTypeId, productId, artifactId),
    getProductTotals: productId => {
      let totals = productCache[productId];
      if (!totals) {
        totals = getProductTotals(byEnvTypeId, productId);
        productCache[productId] = totals;
      }
      return totals;
    },
  };
}

const emptyCatalogueCounts = () => createCatalogueCounts([]);

export {
  TERMINATED,
  buildEnvTypeCountMap,
  getVersionCounts,
  getProductTotals,
  createCatalogueCounts,
  emptyCatalogueCounts,
};
